<template>
  <div>
    <ErrorNotFound v-if="!loading && !product" />
    <div class="h-1">
      <ProgressBar v-if="loading" />
    </div>
    <div v-if="product">
      <RouterView :product="product" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, provide, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { ProductConfigPageVisitReportsCachedEnum, useReviewsProductByIdQuery } from '@/gql/reviews'
import type { ProductData } from '@/gql/reviews'
import { mapTranslationsArrToObj } from '@/helpers/translationsHelper'
import { setFeatureFlagProductCompany } from '@/services/posthog'
import { useRoleRedirect } from '@/services/roleRedirect'
import { reloadReviewsProductKey } from '@/symbols/reviewsReloadKeys'
import ProgressBar from '@/ui/ProgressBar.vue'
import ErrorNotFound from '@/views/ErrorNotFound.vue'

const YES = 'yes'
const PAGE_VISIT_REPORTS_ENABLED = [ProductConfigPageVisitReportsCachedEnum.Basic, ProductConfigPageVisitReportsCachedEnum.Extended]

const props = defineProps<{
  productId: string
}>()

useRoleRedirect('manageReviews')

const variables = computed(() => ({ id: props.productId }))

const { loading, result, refetch } = useReviewsProductByIdQuery(variables, { clientId: 'reviews' })

const product = computed<ProductData | undefined>(() => {
  const data = result.value?.manage?.product
  if (!data) {
    return undefined
  }
  return {
    ...data,
    descriptionTranslationsMap: mapTranslationsArrToObj(data.descriptionTranslations),
    shortDescriptionTranslationsMap: mapTranslationsArrToObj(data.shortDescriptionTranslations),
    linkTextTranslationsMap: mapTranslationsArrToObj(data.linkTextTranslations),
    urlTranslationsMap: mapTranslationsArrToObj(data.urlTranslations),
    disclaimerTextTranslationsMap: mapTranslationsArrToObj(data.disclaimerTextTranslations),
    disclaimerUrlTranslationsMap: mapTranslationsArrToObj(data.disclaimerUrlTranslations),
    noPricingPlansHintTranslationsMap: mapTranslationsArrToObj(data.noPricingPlansHintTranslations),
    ctaSecondaryLabelTranslationsMap: mapTranslationsArrToObj(data.ctaSecondaryLabelTranslations),
    ctaSecondaryUrlTranslationsMap: mapTranslationsArrToObj(data.ctaSecondaryUrlTranslations),
    configFlags: {
      heroActive: data.config.heroImageCached === YES,
      heroEditable: data.config.heroImageAdminCalculated === YES,
      ctaPrimaryActive: data.config.ctaPrimaryCached === YES,
      ctaPrimaryEditable: data.config.ctaPrimaryAdminCalculated === YES,
      ctaSecondaryActive: data.config.ctaSecondaryCached === YES,
      ctaSecondaryEditable: data.config.ctaSecondaryAdminCalculated === YES,
      pricingDisclaimerLinkAllowed: data.config.pricingDisclaimerLinkCached === YES,
      reviewRepliesAllowed: data.config.reviewRepliesAllowedAdminCalculated === YES,
      reviewRemindersAllowed: data.config.reviewRemindersAllowedCached === YES,
      badgesManagementAllowed: data.config.badgesAdminCalculated === YES,
      widgetsManagementAllowed: data.config.widgetsAdminCalculated === YES,
      socialLinksAllowed: data.config.socialLinksAdminCalculated === YES,
      documentsEditable: data.config.downloadsFileUploadAdminCalculated === YES,
      pageVisitReportsEnabled: PAGE_VISIT_REPORTS_ENABLED.includes(data.config.pageVisitReportsCached),
    },
  }
})

provide(reloadReviewsProductKey, refetch)

watch(() => product.value?.companySlug, setFeatureFlagProductCompany, { immediate: true })
onUnmounted(() => setFeatureFlagProductCompany(null))

const { locale } = useI18n()
watch(locale, () => refetch())
</script>
